/** @type {import('tailwindcss').Config} */
import colors from 'tailwindcss/colors'
import forms from '@tailwindcss/forms'

export default {
  content: [
    './pages/**/*.{js,ts,jsx,tsx}',
    './components/**/*.{js,ts,jsx,tsx}',
    './containers/**/*.{js,ts,jsx,tsx}',
    './layout/**/*.{js,ts,jsx,tsx}',
    './src/**/*.{js,ts,jsx,tsx}',
  ],
  theme: {
    extend: {
      backgroundImage: {
        'gradient-01': 'linear-gradient(#3333CC, #3386CC, #33CCCC)',
        'gradient-02': 'linear-gradient(173deg, #8888FC -6.25%, #33CCCC 140.53%)',
        'gradient-05': 'linear-gradient(180deg, #DEFAFA 0%, #D9E2FF 100%)',
        'gradient-03': 'linear-gradient(#F2F5FF, #F5FFFF)',
        'gradient-04': 'linear-gradient(#EBFDFD, #FFFFFF)',
        'gradient-06': 'linear-gradient(135deg, #798DF1 13.87%, #F6A8C2 112.89%)',
        'gradient-07': 'linear-gradient(138deg, #4E46FB 24.36%, #FF5A5A 92.43%)',
        'gradient-rh': 'linear-gradient(173deg, #34D399 -6.25%, #3B82F6 140.53%)',
      },
      colors: {
        primary: {
          DEFAULT: '#3333CC',
          50: '#F6F9FF',
          100: '#F2F5FF',
          200: '#D9E2FF',
          201: '#7C7CE9',
          300: '#ACBCF5',
          400: '#798DF1',
          500: '#4E46FB',
          600: '#3333CC',
          700: '#2121A6',
          800: '#0d0d80',
          900: '#0C0C4D',
          1000: '#0D0D80',
        },
        secondary: {
          100: '#F5FFFF',
          300: '#DEFAFA',
          400: '#A7E8E8',
          500: '#33CCCC',
          700: '#079F9F',
          800: '#1C8282',
          850: '#1A7A7A',
          900: '#005859',
        },
        success: colors.green,
        info: colors.blue,
        danger: colors.red,
        darkblue: '#0C0C4F',
        warning: colors.yellow,
        yellow: {
          DEFAULT: '#F1FE17',
          100: '#fef9c3',
          600: '#ca8a04',
          700: '#a16207',
        },
        achieved: '#F5FFFF',
        transparent: 'transparent',
        'light-green': {
          DEFAULT: '#00CE8D',
        },
        'light-blue': {
          DEFAULT: '#33CCCC',
        },
        muted: '#57586E',
        'second-indigo': '#8888FC',
        red: {
          700: '#E91C1C',
        },
        grey: {
          50: '#F7F8FA',
          100: '#EFF1F4',
          200: '#D9DDE5',
          300: '#BBBFC9',
          400: '#A1A6B3',
          500: '#7B8092',
          600: '#57586E',
          700: '#333348',
        },
        pink: {
          100: '#FDEDF2',
          200: '#FFE3EC',
          300: '#F6A8C2',
          500: '#EC407A',
          600: '#C81953',
          700: '#AE1D4D',
          900: '#6C1E38',
        },
        green: {
          100: '#E9FDEA',
          200: '#D8F5DA',
          300: '#C2F6C6',
          500: '#4ED959',
          700: '#00A606',
          800: '#038105',
          900: '#06660A',
        },
        orange: {
          200: '#FEECDC',
          700: '#FF9C15',
          800: '#E07206',
          900: '#AD5400',
        },
        blue: {
          200: '#E5F0FF',
          300: '#C6DDFF',
        },
        // Semantic colors of the side drawer and its documents (see the design file).
        // Use these roles rather than hex values so a palette change only touches this block.
        drawer: {
          surface: '#F6F7FF',
        },
        document: {
          action: '#1B68FB',
          icon: '#7D7D7D',
          'status-available-bg': '#D6E6F8',
          'status-available-text': '#0062D5',
          'status-pending-bg': '#EFEFEF',
          'status-pending-text': '#6C6C6C',
        },
      },
      dropShadow: {
        3: '0px 10px 20px rgba(0, 0, 0, 0.15)',
      },
      boxShadow: {
        sm: '0px 0px 5px 0px rgba(87, 88, 110, 0.20)',
        md: '0px 2px 10px 0px rgba(87, 88, 110, 0.15)',
        lg: '0px 4px 15px 0px rgba(87, 88, 110, 0.15)',
        xl: '0px 10px 20px -3px rgba(87, 88, 110, 0.30)',
        '2xl': '0px 25px 50px -12px rgba(87, 88, 110, 0.50)',
        buttonSecondary: '0px 0px 25px 0px rgba(167, 232, 232, 1)',
        'document-card': '0px 2px 8px 0px rgba(27, 104, 251, 0.12)',
      },
      padding: {
        input: '13px 16px 10px 16px',
      },
      fontFamily: {
        curecall: ['Inter', 'ui-sans-serif', 'system-ui', 'sans-serif'],
        nexa: ['var(--font-nexa)', 'Inter', 'ui-sans-serif', 'system-ui', 'sans-serif'],
      },
      screens: {
        xs: '400px',
        '2xs': '475px',
        '2md': '900px',
        '2lg': '1130px',
      },
      animation: {
        enter: 'fadeInRight 200ms ease-out',
        leave: 'fadeOutLeft 200ms ease-in forwards',
        'scale-in': 'scale-in 0.3s ease-out',
        'badge-pulse': 'badge-pulse 0.5s ease-in-out 3',
        'fade-in-button': 'fade-in 0.3s ease-out',
        'input-flash': 'input-flash 1.2s ease-out',
      },
      keyframes: {
        fadeInRight: {
          '0%': {
            opacity: '0',
            transform: 'translate(2rem)',
          },
          '100%': {
            opacity: '1',
            transform: 'translate(0)',
          },
        },
        fadeOutLeft: {
          '0%': {
            opacity: '1',
          },
          '100%': {
            opacity: '0',
          },
        },
        'badge-pulse': {
          '0%, 100%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.2)' },
        },
        'scale-in': {
          '0%': {
            transform: 'scaleY(0)',
            transformOrigin: 'top',
            opacity: '0',
          },
          '100%': {
            transform: 'scaleY(1)',
            transformOrigin: 'top',
            opacity: '1',
          },
        },
        'fade-in': {
          '0%': {
            opacity: '0',
            transform: 'translateY(-4px)',
          },
          '100%': {
            opacity: '1',
            transform: 'translateY(0)',
          },
        },
        'input-flash': {
          '0%': {
            boxShadow: '0 0 0 0 rgba(51, 51, 204, 0.7)',
            borderColor: '#3333cc',
            borderWidth: '1px',
            transform: 'scale(1)',
          },
          '25%': {
            transform: 'scale(1.12)',
          },
          '50%': {
            boxShadow: '0 0 0 6px rgba(51, 51, 204, 0)',
            borderColor: '#3333cc',
            borderWidth: '1px',
            transform: 'scale(1)',
          },
          '100%': {
            boxShadow: '0 0 0 0 rgba(51, 51, 204, 0)',
            borderColor: 'rgb(156, 163, 175)',
            borderWidth: '1px',
            transform: 'scale(1)',
          },
        },
      },
    },
  },
  prefix: 'c-',
  plugins: [forms],
}
